<template>
  <v-card flat outlined class="mto-entity-card" :class="{ 'mto-entity-card--nested': nested, 'mto-entity-card--summary-only': summaryOnly }">
    <div v-if="!summaryOnly" class="mto-entity-card__head">
      <div class="mto-entity-card__title-wrap">
        <router-link
          v-if="link"
          :to="link"
          class="mto-entity-card__title mto-entity-card__title--link"
        >
          {{ title }}
        </router-link>
        <div v-else class="mto-entity-card__title">{{ title }}</div>
        <div v-if="subtitle" class="mto-entity-card__subtitle">{{ subtitle }}</div>
      </div>
      <v-chip v-if="badge" x-small outlined color="primary" class="mto-entity-card__badge">
        {{ badge }}
      </v-chip>
    </div>

    <div v-if="agentName" class="mto-entity-card__meta">
      <v-icon x-small class="mr-1">mdi-account-tie-outline</v-icon>
      {{ agentName }}
    </div>
    <div v-if="mastersText" class="mto-entity-card__meta">
      <v-icon x-small class="mr-1">mdi-account-wrench-outline</v-icon>
      {{ mastersText }}
    </div>

    <div class="mto-entity-card__stats">
      <div
        v-for="stat in stats"
        :key="stat.key"
        class="mto-entity-card__stat"
        :class="`mto-entity-card__stat--${stat.tone || 'default'}`"
      >
        <div class="mto-entity-card__stat-value">{{ stat.value }}</div>
        <div class="mto-entity-card__stat-label">{{ stat.label }}</div>
      </div>
    </div>

    <div v-if="equipmentText" class="mto-entity-card__equipment">
      {{ equipmentText }}
    </div>
  </v-card>
</template>

<script>
const DEFAULT_LABELS = {
  count: 'Домофоны',
  st_2: 'В сети',
  st_null: 'Не в сети',
  'st_-1': 'Не зарег.',
  st_5: 'Не уст.',
  st_6: 'Без сети',
  st_7: 'Пусконал.',
  st_8: 'Старые',
  st_9: 'Ожидание',
};

export default {
  name: 'MtoIndicatorsEntityCard',
  props: {
    title: {
      type: String,
      default: '',
    },
    subtitle: {
      type: String,
      default: '',
    },
    badge: {
      type: String,
      default: '',
    },
    agentName: {
      type: String,
      default: '',
    },
    masters: {
      type: Array,
      default: () => [],
    },
    doorphones: {
      type: Object,
      default: () => ({}),
    },
    barriersCount: {
      type: Number,
      default: null,
    },
    gatesCount: {
      type: Number,
      default: null,
    },
    equipmentCounts: {
      type: Object,
      default: null,
    },
    statusDescription: {
      type: Object,
      default: () => ({}),
    },
    nested: {
      type: Boolean,
      default: false,
    },
    summaryOnly: {
      type: Boolean,
      default: false,
    },
    link: {
      type: String,
      default: '',
    },
  },
  computed: {
    mastersText () {
      if (!this.masters || !this.masters.length) return '';
      return this.masters.map(master => master.name).join(', ');
    },
    stats () {
      const items = [];
      const doorphones = this.doorphones || {};

      const pushStat = (key, tone) => {
        if (doorphones[key] === undefined) return;
        items.push({
          key: `dp-${key}`,
          label: this.statusDescription[key] || DEFAULT_LABELS[key] || key,
          value: doorphones[key],
          tone,
        });
      };

      pushStat('count', 'primary');
      pushStat('st_2', 'success');
      pushStat('st_null', 'warning');
      pushStat('st_-1', 'muted');

      if (this.barriersCount !== null) {
        items.push({
          key: 'barriers',
          label: 'Шлагбаумы',
          value: this.barriersCount,
          tone: 'default',
        });
      }

      if (this.gatesCount !== null) {
        items.push({
          key: 'gates',
          label: 'Калитки',
          value: this.gatesCount,
          tone: 'default',
        });
      }

      return items;
    },
    equipmentText () {
      if (!this.equipmentCounts) return '';
      const counts = this.equipmentCounts;
      const parts = [];
      if (counts.doorphones) parts.push(`дом. ${counts.doorphones}`);
      if (counts.additional_entrances) parts.push(`доп. входы ${counts.additional_entrances}`);
      if (counts.gates_doorphones) parts.push(`кал. дом. ${counts.gates_doorphones}`);
      if (counts.gates_readers) parts.push(`кал. счит. ${counts.gates_readers}`);
      if (counts.barriers) parts.push(`шл. ${counts.barriers}`);
      if (counts.apartments) parts.push(`кв. ${counts.apartments}`);
      if (counts.apartments_with_phone_setup) parts.push(`возм. трубки ${counts.apartments_with_phone_setup}`);
      if (!parts.length && !counts.total) return '';
      return `Показатели: ${parts.join(', ') || counts.total}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.mto-entity-card {
  height: 100%;
  border-radius: 12px;
  padding: 14px 14px 12px;
  background: #fff;
}

.mto-entity-card--nested {
  background: #f8fafc;
}

.mto-entity-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.mto-entity-card__title-wrap {
  min-width: 0;
}

.mto-entity-card__title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #263238;
  line-height: 1.3;
}

.mto-entity-card__title--link {
  color: #1976d2;
  text-decoration: none;
}

.mto-entity-card__title--link:hover {
  text-decoration: underline;
}

.mto-entity-card__subtitle {
  margin-top: 2px;
  font-size: 0.8125rem;
  color: #78909c;
}

.mto-entity-card__badge {
  flex-shrink: 0;
}

.mto-entity-card__meta {
  display: flex;
  align-items: center;
  font-size: 0.8125rem;
  color: #607d8b;
  margin-bottom: 4px;
  line-height: 1.35;
}

.mto-entity-card__stats {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.mto-entity-card--summary-only .mto-entity-card__stats {
  margin-top: 0;
}

.mto-entity-card__stat {
  flex: 1 1 72px;
  min-width: 72px;
  max-width: 120px;
  padding: 8px 6px;
  border-radius: 10px;
  background: #f5f7fa;
  text-align: center;
}

.mto-entity-card__stat--primary {
  background: rgba(25, 118, 210, 0.08);
}

.mto-entity-card__stat--success {
  background: rgba(76, 175, 80, 0.1);
}

.mto-entity-card__stat--warning {
  background: rgba(255, 152, 0, 0.1);
}

.mto-entity-card__stat--muted {
  background: rgba(120, 144, 156, 0.12);
}

.mto-entity-card__stat-value {
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.2;
  color: #263238;
}

.mto-entity-card__stat-label {
  margin-top: 2px;
  font-size: 0.6875rem;
  line-height: 1.2;
  color: #78909c;
}

.mto-entity-card__equipment {
  margin-top: 10px;
  font-size: 0.75rem;
  color: #78909c;
  line-height: 1.4;
}
</style>
